import {
  DeviceLabels,
  useAudioVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import {
  MeetingSessionConfiguration,
  MessagingSessionConfiguration,
} from "amazon-chime-sdk-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import useMessagingManager from "./useMessagingManager";

const useChime = ({
  meeting,
  meetingAttendee,
  messagingUserArn,
  messagingChannelArn,
  onAttendeePresenceChange,
  onGoodConnection,
  onNetworkError,
  onPoorConnection,
}) => {
  const [hasJoined, setHasJoined] = useState();

  const meetingManager = useMeetingManager();
  const messagingManager = useMessagingManager();

  const audioVideo = useAudioVideo();

  const meetingEventsObserver = useMemo(
    () => ({
      eventDidReceive(name) {
        switch (name) {
          case "signalingDropped":
            onNetworkError();
            break;
          default:
            break;
        }
      },
    }),
    [onNetworkError],
  );

  const audioVideoObserver = useMemo(
    () => ({
      connectionDidBecomeGood() {
        onGoodConnection();
      },
      connectionDidBecomePoor() {
        onPoorConnection();
      },
    }),
    [onGoodConnection, onPoorConnection],
  );

  const joinMeeting = useCallback(async () => {
    const meetingSessionConfiguration = new MeetingSessionConfiguration(
      meeting,
      meetingAttendee,
    );
    const messagingSessionConfiguration = new MessagingSessionConfiguration(
      messagingUserArn,
      crypto.randomUUID(),
      undefined,
      messagingManager.client,
    );

    try {
      await meetingManager.join(meetingSessionConfiguration, {
        deviceLabels: DeviceLabels.Audio,
      });
      messagingManager.join(messagingSessionConfiguration);
      await meetingManager.start();
      messagingManager.start();
      setHasJoined(true);
    } catch (error) {
      console.error("Failed to join meeting", error);
    }
  }, [
    meetingManager,
    meeting,
    meetingAttendee,
    messagingManager,
    messagingUserArn,
  ]);

  const muteLocalAudio = useCallback(() => {
    const audioElement = document.getElementsByTagName("audio")?.[0];

    if (audioElement) {
      audioVideo?.realtimeMuteLocalAudio();
      audioVideo?.unbindAudioElement(audioElement);
    }
  }, [audioVideo]);

  const unMuteLocalAudio = useCallback(() => {
    const audioElement = document.getElementsByTagName("audio")?.[0];

    if (audioElement) {
      audioVideo?.realtimeUnmuteLocalAudio();
      audioVideo?.bindAudioElement(audioElement);
    }
  }, [audioVideo]);

  const leaveMeeting = useCallback(async () => {
    await meetingManager.leave();
    messagingManager.leave();
    setHasJoined(false);
  }, [meetingManager, messagingManager]);

  useEffect(() => {
    if (hasJoined) {
      meetingManager?.meetingSession?.eventController?.addObserver(
        meetingEventsObserver,
      );
      return () => {
        meetingManager?.meetingSession?.eventController?.removeObserver(
          meetingEventsObserver,
        );
      };
    }
  }, [hasJoined, meetingEventsObserver, meetingManager]);

  useEffect(() => {
    audioVideo?.realtimeSubscribeToAttendeeIdPresence(onAttendeePresenceChange);
    audioVideo?.addObserver(audioVideoObserver);
    return () => {
      audioVideo?.realtimeUnsubscribeToAttendeeIdPresence(
        onAttendeePresenceChange,
      );
      audioVideo?.removeObserver(audioVideoObserver);
    };
  }, [audioVideo, audioVideoObserver, onAttendeePresenceChange]);

  const listChannelMessages = useCallback(
    async (nextToken = undefined) => {
      if (messagingUserArn && messagingChannelArn) {
        return messagingManager.listChannelMessages({
          ChannelArn: messagingChannelArn,
          ChimeBearer: messagingUserArn,
          NextToken: nextToken,
          SortOrder: "ASCENDING",
        });
      }
    },
    [messagingChannelArn, messagingManager, messagingUserArn],
  );

  const getReadMarkerTimestamp = useCallback(async () => {
    if (messagingUserArn) {
      const response = await messagingManager.getReadMarkerTimestamp({
        AppInstanceUserArn: messagingUserArn,
        ChimeBearer: messagingUserArn,
      });

      return response?.ChannelMemberships?.[0]?.AppInstanceUserMembershipSummary
        ?.ReadMarkerTimestamp;
    }
  }, [messagingManager, messagingUserArn]);

  const updateReadMarkerTimestamp = useCallback(async () => {
    if (messagingUserArn && messagingChannelArn) {
      await messagingManager.updateReadMarkerTimestamp({
        ChimeBearer: messagingUserArn,
        ChannelArn: messagingChannelArn,
      });

      return true;
    }
  }, [messagingChannelArn, messagingManager, messagingUserArn]);

  const sendChannelMessage = useCallback(
    async ({ content, metadata, persistence, type }) => {
      if (messagingUserArn && messagingUserArn) {
        return messagingManager.sendChannelMessage({
          ChannelArn: messagingChannelArn,
          ChimeBearer: messagingUserArn,
          Content: content,
          Metadata: metadata,
          Persistence: persistence,
          Type: type,
        });
      }
    },
    [messagingChannelArn, messagingManager, messagingUserArn],
  );

  const addMessagingObserver = useCallback(
    (observer) => {
      messagingManager.messagingSession?.addObserver(observer);
    },
    [messagingManager],
  );

  const removeMessagingObserver = useCallback(
    (observer) => {
      messagingManager.messagingSession?.removeObserver(observer);
    },
    [messagingManager],
  );

  return {
    audioVideo,
    hasJoined,
    joinMeeting,
    leaveMeeting,
    addMessagingObserver,
    removeMessagingObserver,
    listChannelMessages,
    sendChannelMessage,
    getReadMarkerTimestamp,
    updateReadMarkerTimestamp,
    muteLocalAudio,
    unMuteLocalAudio,
  };
};

export default useChime;
